import React, { useState } from 'react';

import { getContext, useCombinedRefs } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';
import { handleKeyDown, handleFocus, CONSTANTS } from 'common/ListKeyboardNav';
import Styled from './styles';

import HorizontalListItem from './HorizontalListItem';
import HorizontalListButton from './HorizontalListButton';

const { LIST_WRAPPER } = CONSTANTS;

const ELEVATION_INDEX = 0;

const STYLE = {
  borderRadius: '1.125rem'
};
export interface HorizontalListProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the HorizontalList, consider using `HorizontalListItem` and `HorizontalListButton`.  */
  children: React.ReactNode;
  /** Enable/Disable keyboard navigation */
  keyboardNavigation?: boolean;
  /** Override `Elevation` props that act as a container. */
  elevationProps?: Partial<ElevationProps>;
}

/** Component for displaying a horizontal list of items.
 * Any other props than `children` will be provided to the wrapper element.
 */
const HorizontalList = React.forwardRef<HTMLElement, HorizontalListProps>((props: HorizontalListProps, ref) =>
  getContext(({ RTL }) => {
    const {
      children,
      elevationProps,
      ...otherProps
    } = props;
    const [wasTabPressed, setWasTabPressed] = useState(false);
    const listRef = React.createRef();
    const combinedRef = useCombinedRefs(ref, listRef);
    // Callback function to keep track of when Tab was pressed. Use this to set
    // the tabIndex of the list box. If not, the list box will get focus when the
    // tab key is pressed while focused on any list items.
    const tabPressed = (val) => {
      setWasTabPressed(val);
    };

    // Need this function to reset the tabpressed state or the list box will be left with tabIndex -1.
    const handleBlur = () => {
      setWasTabPressed(false);
    };

    return (
      <Elevation
        style={STYLE}
        elevationIndex={ELEVATION_INDEX}
        dark
        {...elevationProps}
      >
        <Styled.HorizontalList
          tabIndex={wasTabPressed ? -1 : 0}
          className={LIST_WRAPPER}
          role="list"
          ref={combinedRef}
          $rtl={RTL}
          {...otherProps}
          onKeyDown={handleKeyDown(props, combinedRef, false, tabPressed)}
          onFocus={handleFocus(props, combinedRef, wasTabPressed, tabPressed)}
          onBlur={handleBlur}
        >
          <Styled.ListUnOrdered>
            {children}
          </Styled.ListUnOrdered>
        </Styled.HorizontalList>
      </Elevation>
    );
  }));

HorizontalList.displayName = 'HorizontalList';

HorizontalList.defaultProps = {
  keyboardNavigation: true,
  elevationProps: {}
};

export {
  HorizontalList as default,
  HorizontalListItem,
  HorizontalListButton
};
